/**
 * Kaching bundle price — reflect the selected bundle tier in the PDP price.
 *
 * The Kaching Bundles app-embed injects a "Buy More, Save More" deal picker at
 * runtime and drives the buy form's quantity to the selected tier, but the
 * theme's own <product-price> never reflects the deal — it keeps showing the
 * raw variant price. This controller rewrites the main <product-price> to show
 * the selected shopper-visible tier's PER-UNIT price with the single-unit list
 * struck through, plus an "N% OFF" badge, matching snippets/price.liquid's
 * markup. Main PDP prices follow the selected tier; unmarked legacy prices
 * fall back to the first tier.
 *
 * Sources of truth:
 *   - The current widget's `deal-block` attribute, whose `dealBars` array uses
 *     Kaching's live { id, quantity, discountType, discountValue } shape.
 *   - window.__kachingCartBundlesDeals[].dealBars[] — { id, quantity, discount }
 *     remains a fallback for the legacy app shape. A specific discount value
 *     is the TOTAL bundle price in DOLLARS (theme prices are in cents).
 *   - The on-page radios `input[name^="kaching-bundles-deal"]`; their DOM order
 *     matches the visible tier order and each value === its dealBar id.
 *   - Base variant price/compare (cents) + a11y labels come from data-* attrs
 *     that snippets/price.liquid stamps on [ref="priceContainer"] (PDP only).
 *
 * Progressive enhancement: with no JS (or if Kaching never loads) the SSR
 * variant price is left untouched.
 */

const RADIO_SELECTOR = 'input[name^="kaching-bundles-deal"]';
const BUNDLE_BLOCK_SELECTOR = "kaching-bundles-block[deal-block]";
const INITIALIZED_EVENT = "kaching-bundles-initialized";
const INITIALIZATION_TIMEOUT = 5000;

/** Cards / modals whose prices must NOT be touched (only the main PDP price). */
const CARD_SELECTOR =
  "product-card, .product-card, .resource-card, quick-add-component, quick-add-dialog, dialog";

/**
 * Currency formatting. Hardcode the `en-US` locale (as eden-buy.js and
 * fts-bundle-builder.js do) so the overlay's number format stays consistent
 * with the SSR `money` filter and other JS price displays regardless of the
 * shopper's browser locale; the currency still tracks the active market.
 */
const formatMoney = (cents) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: window.Shopify?.currency?.active || "USD",
  }).format(Number(cents) / 100);

const escapeHtml = (value) =>
  String(value).replace(
    /[&<>"']/g,
    (char) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[char],
  );

export class KachingBundlePrice {
  /** @type {HTMLElement} the main PDP <product-price> element */
  #priceElement;
  /** @type {MutationObserver | null} */
  #observer = null;
  /** Native markup belongs to each server-rendered variant price container. */
  #nativeMarkup = new WeakMap();
  #stopWaiting = () => {};

  /** @param {HTMLElement} priceElement */
  constructor(priceElement) {
    this.#priceElement = priceElement;
  }

  init() {
    this.#waitForKaching(() => this.#start());
  }

  destroy() {
    this.#stopWaiting();
    this.#observer?.disconnect();
    document.removeEventListener("change", this.#onDealChange);
  }

  /**
   * Wait for Kaching readiness (deal config and radios available in the DOM).
   * The app emits its initialized event once its widget module mounts; a
   * short-lived DOM observer also covers versions that insert the radio after
   * that event. On timeout we leave the SSR price untouched (also the correct
   * outcome for a product with no bundle).
   * @param {() => void} onReady
   */
  #waitForKaching(onReady) {
    const ready = () => {
      const scope = this.#priceElement.closest(
        '[data-purchase-context="embedded"]',
      )
        ? this.#priceElement.closest(".shopify-section")
        : document;
      const firstRadio = scope?.querySelector(RADIO_SELECTOR);
      if (!firstRadio) return false;

      const block = firstRadio.closest(BUNDLE_BLOCK_SELECTOR);
      return (
        block?.getAttribute("deal-block") != null ||
        Array.isArray(window.__kachingCartBundlesDeals)
      );
    };

    if (ready()) {
      onReady();
      return;
    }

    let finished = false;
    let observer = null;
    let timeout = 0;

    const cleanup = () => {
      window.removeEventListener(INITIALIZED_EVENT, startIfReady);
      window.removeEventListener("pagehide", stopWaiting);
      observer?.disconnect();
      window.clearTimeout(timeout);
    };

    const startIfReady = () => {
      if (finished || !ready()) return;

      finished = true;
      cleanup();
      onReady();
    };

    const stopWaiting = () => {
      if (finished) return;

      finished = true;
      cleanup();
    };
    this.#stopWaiting = stopWaiting;

    window.addEventListener(INITIALIZED_EVENT, startIfReady);
    window.addEventListener("pagehide", stopWaiting, { once: true });

    observer = new MutationObserver(startIfReady);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
    });

    // Stop broad DOM scanning, but allow a slow widget to announce readiness.
    // The event listener is removed on initialization or pagehide.
    timeout = window.setTimeout(() => {
      observer?.disconnect();
      observer = null;
    }, INITIALIZATION_TIMEOUT);

    // Close the gap if Kaching initialized between the first readiness check
    // and listener registration.
    startIfReady();
  }

  #start() {
    const container = this.#priceElement.querySelector(
      '[ref="priceContainer"]',
    );
    const parent = container?.parentNode;
    if (!parent) return;

    // Kaching's radios are app-injected and live outside our subtree. A change
    // refreshes the selected tier on PDPs and the first tier for legacy markup.
    document.addEventListener("change", this.#onDealChange);

    // Variant selection: product-price.js replaces the whole [ref="priceContainer"]
    // with fresh SSR variant HTML. Observe the container's parent for that swap
    // (childList only) and re-apply. Our own writes mutate the container's
    // descendants — never `parent`'s direct children — so they don't re-trigger.
    this.#observer = new MutationObserver(() => {
      if (!this.#priceElement.isConnected) {
        this.#observer.disconnect();
        document.removeEventListener("change", this.#onDealChange);
        return;
      }
      this.#apply();
    });
    this.#observer.observe(parent, { childList: true });
    if (container.hasAttribute("data-first-tier-sale-badge")) {
      const section = this.#priceElement.closest(".shopify-section");
      if (section) {
        this.#observer.observe(section, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
          attributeFilter: ["deal-block", "checked", "class"],
        });
      }
    }

    this.#apply();
  }

  #onDealChange = (event) => {
    const target = event.target;
    if (
      target instanceof HTMLInputElement &&
      target.name?.startsWith("kaching-bundles-deal")
    ) {
      this.#apply();
    }
  };

  /**
   * The dealBar for a radio, read live from its Kaching block each time. The
   * legacy global remains a fallback for older app versions. Reading live
   * rather than caching means later changes to the deal data are picked up.
   * @param {HTMLInputElement} radio
   * @param {string} id
   * @returns {object | null}
   */
  #findBar(radio, id) {
    const block = radio.closest(BUNDLE_BLOCK_SELECTOR);
    const dealBlock = block?.getAttribute("deal-block");
    if (dealBlock) {
      try {
        const config = JSON.parse(dealBlock);
        const configuredBar = config.dealBars?.find(
          (bar) => String(bar?.id) === id,
        );
        if (configuredBar) return configuredBar;
      } catch {
        // The legacy global below remains a safe fallback for malformed data.
      }
    }

    for (const deal of window.__kachingCartBundlesDeals ?? []) {
      for (const bar of deal?.dealBars ?? []) {
        if (String(bar?.id) === id) return bar;
      }
    }
    return null;
  }

  /** @param {HTMLElement} container */
  #restoreNativePrice(container) {
    const html = this.#nativeMarkup.get(container);
    if (html !== undefined && container.innerHTML !== html) {
      container.innerHTML = html;
    }
  }

  /** Recompute the applicable offer's per-unit price and rewrite the price DOM. */
  #apply() {
    const container = this.#priceElement.querySelector(
      '[ref="priceContainer"]',
    );
    if (!container) return;
    if (!this.#nativeMarkup.has(container)) {
      this.#nativeMarkup.set(container, container.innerHTML);
    }

    const variantPrice = Number(container.dataset.variantPrice);
    const variantCompareAt = Number(container.dataset.variantCompareAt) || 0;
    if (!Number.isFinite(variantPrice) || variantPrice <= 0) {
      this.#restoreNativePrice(container);
      return;
    }

    // The struck-through "list" is the variant's regular price.
    const base = Math.max(variantPrice, variantCompareAt);

    // Main PDP prices follow the selected offer; legacy markup keeps the first tier.
    const followSelection = container.hasAttribute(
      "data-first-tier-sale-badge",
    );
    const scope = followSelection
      ? this.#priceElement.closest(".shopify-section")
      : document;
    const offerRadio =
      (followSelection && scope?.querySelector(`${RADIO_SELECTOR}:checked`)) ||
      scope?.querySelector(RADIO_SELECTOR);
    if (!(offerRadio instanceof HTMLInputElement)) {
      this.#restoreNativePrice(container);
      return;
    }

    const offerId = offerRadio.value;
    const bar = offerId ? this.#findBar(offerRadio, offerId) : null;
    if (!bar) {
      this.#restoreNativePrice(container);
      return;
    }

    const qty = Number(bar.quantity);
    if (!Number.isFinite(qty) || qty <= 0) {
      this.#restoreNativePrice(container);
      return;
    }

    // Per-unit: a 'specific' discount `value` is the TOTAL bundle price in
    // dollars. A no-discount tier uses the variant's own price. Invalid or
    // unrecognised deal data leaves Shopify's server-rendered price untouched.
    const discountType = bar.discount?.type ?? bar.discountType;
    const discountValue = bar.discount?.value ?? bar.discountValue;

    let perUnit = variantPrice;
    if (discountType === "specific") {
      const bundlePrice = Number(discountValue);
      if (!Number.isFinite(bundlePrice) || bundlePrice < 0) {
        this.#restoreNativePrice(container);
        return;
      }
      perUnit = Math.round((bundlePrice * 100) / qty);
    } else if (
      bar.discount != null ||
      (discountType != null && discountType !== "default")
    ) {
      this.#restoreNativePrice(container);
      return;
    }

    const label =
      offerRadio
        .closest(".kaching-bundles__bar")
        ?.querySelector(".kaching-bundles__bar-label")?.textContent ?? "";
    const percentage = label.match(/(?:^|\s)(\d+(?:[.,]\d+)?)\s*%/);
    const saleLabel =
      percentage &&
      Number(percentage[1].replace(",", ".")) > 0 &&
      Number(percentage[1].replace(",", ".")) <= 100
        ? `SALE · ${percentage[1]}% OFF`
        : "";
    const html =
      perUnit < base
        ? this.#saleMarkup(container, perUnit, base, saleLabel)
        : `<span class="price">${escapeHtml(formatMoney(perUnit))}${followSelection ? " each" : ""}</span>`;

    if (container.innerHTML !== html) container.innerHTML = html;
  }

  /**
   * On-sale markup mirroring snippets/price.liquid (role="group" wrappers,
   * .price--on-sale, .compare-at-price, and PDP percent badge).
   * @param {HTMLElement} container
   * @param {number} perUnit sale price in cents
   * @param {number} base list price in cents
   * @param {string} saleLabel resolved offer savings label from Kaching
   */
  #saleMarkup(container, perUnit, base, saleLabel) {
    const { salePriceFirst, labelSale, labelRegular, salePriceColor } =
      container.dataset;

    const colorStyle = salePriceColor
      ? ` style="--sale-price-color: ${escapeHtml(salePriceColor)}"`
      : "";

    const priceGroup = `<span role="group"><span class="visually-hidden">${escapeHtml(
      labelSale || "",
    )}&nbsp;</span><span class="price price--on-sale"${colorStyle}>${escapeHtml(
      formatMoney(perUnit) +
        (container.hasAttribute("data-first-tier-sale-badge") ? " each" : ""),
    )}</span></span>`;

    const compareGroup = `<span role="group"><span class="visually-hidden">${escapeHtml(
      labelRegular || "",
    )}&nbsp;</span><span class="compare-at-price">${escapeHtml(
      formatMoney(base),
    )}</span></span>`;

    const save = base - perUnit;
    const percent = Math.round((save / base) * 100);
    const badge = `<span class="price-inline-badge price-inline-badge--percent">${percent}% OFF</span>`;

    // show_sale_price_first == true -> [sale][compare]; false -> [compare][sale].
    const order =
      salePriceFirst === "true"
        ? [priceGroup, compareGroup].join(" ")
        : [compareGroup, priceGroup].join(" ");

    if (container.hasAttribute("data-first-tier-sale-badge")) {
      const saleBadge = saleLabel
        ? `<span class="fts-first-tier-sale-badge">${escapeHtml(saleLabel)}</span>`
        : "";
      return salePriceFirst === "true"
        ? priceGroup + saleBadge + " " + compareGroup
        : compareGroup + " " + priceGroup + saleBadge;
    }
    return order + badge;
  }
}

const init = () => {
  if (window.__kachingBundlePriceInit) return;

  // Patch every main-column <product-price> (a merchant could add more than one
  // Price block), excluding recommendation / quick-add card prices.
  const priceElements = [
    ...document.querySelectorAll("product-price[data-product-id]"),
  ]
    .filter((el) => !el.closest(CARD_SELECTOR))
    .filter((el) => !el.closest('[data-purchase-context="embedded"]'))
    .filter((el) =>
      el.closest(
        '.product-details, [data-testid="product-information-details"]',
      ),
    );

  if (priceElements.length === 0) return;

  window.__kachingBundlePriceInit = true;
  for (const element of priceElements) new KachingBundlePrice(element).init();
};

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init, { once: true });
} else {
  init();
}
